import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// URL base del backend
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://tu-abogado-inmigracion-ws-ltlap.ondigitalocean.app/api/';

// Crear instancia de axios con configuración base
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 segundos
});

// Flag para evitar múltiples redirecciones simultáneas
let isRedirecting = false;

// Interceptor para agregar el token a todas las peticiones
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
      
      // Debug: verificar que el token se está agregando
      if (process.env.NODE_ENV === 'development' && config.url?.includes('/auth/users/me/')) {
        console.log('Interceptor: Agregando token a la petición:', {
          url: config.url,
          token: token ? `${token.substring(0, 20)}...` : 'NO HAY TOKEN',
          header: config.headers.Authorization ? 'Bearer ...' : 'NO AGREGADO'
        });
      }
    } else if (process.env.NODE_ENV === 'development' && config.url?.includes('/auth/users/me/')) {
      console.warn('⚠️ No hay token en localStorage para la petición a /auth/users/me/');
    }
    
    // IMPORTANTE: Si el data es FormData, eliminar Content-Type para que el navegador lo establezca automáticamente
    // con el boundary correcto para multipart/form-data
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
      
      // Debug: verificar FormData
      if (process.env.NODE_ENV === 'development') {
        console.log('📤 Interceptor - FormData detectado:', {
          url: config.url,
          method: config.method,
          hasToken: !!token,
          formDataKeys: Array.from((config.data as FormData).keys()),
        });
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas y errores
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Si el token expiró (401) y no es una petición de login
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Evitar múltiples redirecciones
      if (isRedirecting) {
        return Promise.reject(error);
      }

      const token = localStorage.getItem('token');
      
      if (token) {
        // Marcar que ya intentamos refrescar
        originalRequest._retry = true;
        
        // Limpiar tokens y datos del usuario
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('login');
        
        // Redirigir al login solo si no estamos ya en la página de login
        if (!window.location.pathname.includes('/login')) {
          isRedirecting = true;
          
          // Pequeño delay para evitar problemas de navegación
          setTimeout(() => {
            window.location.href = `${process.env.PUBLIC_URL}/login`;
            isRedirecting = false;
          }, 100);
        }
      }
    }

    // Manejar otros errores comunes
    const status = error.response?.status;
    if (status === 403) {
      // 403 es un error de autorización (permisos), no de autenticación
      // No redirigir al login, solo loggear
      console.warn('⚠️ Acceso denegado (403): No tienes permisos para esta acción', {
        url: originalRequest.url,
        method: originalRequest.method,
      });
    } else if (status === 404) {
      console.warn('⚠️ Recurso no encontrado (404):', originalRequest.url);
    } else if (status && status >= 500) {
      console.error('❌ Error del servidor (5xx):', {
        status,
        url: originalRequest.url,
        data: error.response?.data,
      });
    }

    return Promise.reject(error);
  }
);

export default apiClient;


